import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import {
  aggregateByGranularity,
  formatYen,
  GRANULARITY_OPTIONS,
} from '../../lib/revenue-aggregation.js';
import type { Granularity } from '../../lib/revenue-aggregation.js';
import { AXIS_TICK_STYLE, GRID_PROPS, LEGEND_PROPS } from '../../lib/chart-theme.js';
import { ChartFilterBar } from '../chart/ChartFilterBar.js';
import type { RevenueMetricKey, RevenueRecord } from '../../types/revenue.js';

/** 積み上げ棒グラフの対象8カテゴリ（売上ブロック全指標）と表示色。labelKey は描画側で t() する翻訳キー。 @graph-connects none */
const STACKED_KEYS: { key: RevenueMetricKey; labelKey: string; color: string }[] = [
  { key: 'subscriptionFeeMonthly', labelKey: 'revenue.subscriptionFeeMonthly', color: '#0017C1' },
  {
    key: 'subscriptionFeeBimonthly',
    labelKey: 'revenue.subscriptionFeeBimonthly',
    color: '#3460FB',
  },
  { key: 'ticketPurchaseRevenue', labelKey: 'revenue.ticketPurchaseRevenue', color: '#7096F8' },
  { key: 'penaltyFee', labelKey: 'revenue.penaltyFee', color: '#C5D7FB' },
  { key: 'itemPurchase', labelKey: 'revenue.itemPurchase', color: '#00B894' },
  { key: 'expiredRevenue', labelKey: 'revenue.expiredRevenue', color: '#FDCB6E' },
  { key: 'badDebt', labelKey: 'revenue.badDebt', color: '#FE3939' },
  { key: 'refund', labelKey: 'revenue.refund', color: '#E17055' },
];

/** badDebt/refund は控除項目のため、積み上げグラフ上は負値としてゼロ軸の下に表示する。 @graph-connects none */
const DEDUCTION_KEYS: readonly RevenueMetricKey[] = ['badDebt', 'refund'];

/** @graph-connects none */
interface TooltipEntry {
  value?: number;
  name?: string;
  color?: string;
}

/** @graph-connects none */
export function ChartTooltipContent({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
}) {
  const { t } = useTranslation();
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce((sum, entry) => sum + (entry.value ?? 0), 0);
  return (
    <div
      style={{
        background: 'var(--color-bg-elevated, #fff)',
        border: '1px solid var(--color-border)',
        borderRadius: '8px',
        padding: '8px 12px',
        fontSize: '12px',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--color-text-primary)' }}>
        {label}
      </div>
      {payload.map((entry) => (
        <div key={entry.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <span style={{ color: entry.color }}>{entry.name}</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatYen(entry.value ?? 0)}</span>
        </div>
      ))}
      <div
        style={{
          borderTop: '1px solid var(--color-border)',
          marginTop: 4,
          paddingTop: 4,
          display: 'flex',
          justifyContent: 'space-between',
          fontWeight: 600,
          color: 'var(--color-text-primary)',
        }}
      >
        <span>{t('common.total')}</span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatYen(total)}</span>
      </div>
    </div>
  );
}

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上集計のチャートタブ。日次/週次/月次/四半期/年次の粒度切替と、売上8カテゴリの積み上げ棒グラフ（貸倒/返金は控除としてゼロ軸下に表示）、ホバー時に合計金額を表示する。
 * @graph-connects aggregateByGranularity [calls] 粒度別集約
 */
export function RevenueChartView({ rows }: { rows: RevenueRecord[] }) {
  const { t } = useTranslation();
  const [granularity, setGranularity] = useState<Granularity>('daily');

  const chartRows = useMemo(() => {
    const buckets = aggregateByGranularity(rows, granularity);
    return buckets.map((b) => ({ label: b.label, ...b.record }));
  }, [rows, granularity]);

  return (
    <div className="mt-6 space-y-4">
      <ChartFilterBar
        label={t('common.granularity')}
        options={GRANULARITY_OPTIONS}
        value={granularity}
        onChange={setGranularity}
      />

      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
          {t('revenue.categoryTrend')}
        </h3>
        <div style={{ width: '100%', height: 360 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartRows} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
              <CartesianGrid {...GRID_PROPS} />
              <XAxis dataKey="label" tick={AXIS_TICK_STYLE} tickLine={false} />
              <YAxis
                tick={AXIS_TICK_STYLE}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => v.toLocaleString('ja-JP')}
              />
              <Tooltip content={<ChartTooltipContent />} />
              <Legend {...LEGEND_PROPS} />
              {STACKED_KEYS.map(({ key, labelKey, color }) =>
                DEDUCTION_KEYS.includes(key) ? (
                  <Bar
                    key={key}
                    dataKey={(row: RevenueRecord) => -row[key]}
                    name={t(labelKey)}
                    stackId="revenue"
                    fill={color}
                  />
                ) : (
                  <Bar key={key} dataKey={key} name={t(labelKey)} stackId="revenue" fill={color} />
                ),
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}
